<template>
  <div class="review">
    <div class="review-head">
      <div class="review-head-left">
        <span class="review-rating">
          <feather-icon
            v-for="i in 5"
            :key="i"
            icon="StarIcon"
            size="18"
            :class="['star', (i <= review.rating) ? 'filled' : '']"
          />
        </span>
        <span class="review-date">
          {{ formatDate('dd-MM-yyyy at hh:mm', new Date(review.dateAt)) }}
        </span>
      </div>
      <!-- Once the window has closed the action is genuinely unavailable, so the button
           is disabled and the reason sits beside it rather than behind a modal the user
           can no longer reach. No aria-describedby: a disabled button is out of the tab
           order and skipped by most virtual cursors, so the description would never be
           announced. The text is a sibling instead, read as part of the card. -->
      <div class="review-head-right">
        <span
          v-if="exceptionDisabled"
          class="review-spent-reason"
        >
          Can’t be removed after 1 month or once you’ve replied
        </span>
        <ds-button
          variant="secondary"
          size="sm"
          :disabled="exceptionDisabled"
          @click="$emit('exception', review)"
        >
          Not applicable to us
        </ds-button>
      </div>
    </div>

    <div
      v-if="hasTags"
      class="review-tags"
    >
      <ds-badge
        v-for="tag in review.negativeTags"
        :key="tag"
        variant="danger"
      >
        {{ tag }}
      </ds-badge>
      <ds-badge
        v-for="tag in review.positiveTags"
        :key="tag"
        variant="success"
      >
        {{ tag }}
      </ds-badge>
    </div>

    <p
      class="review-explanation"
      :class="{ empty: !review.explanation }"
    >
      {{ review.explanation ? review.explanation : 'No message' }}
    </p>

    <div
      v-if="review.replyMessage"
      class="review-reply"
    >
      <div class="review-reply-head">
        Your reply
        <span class="review-reply-date">
          {{ formatDate('dd-MM-yyyy at hh:mm', new Date(review.replyAt)) }}
        </span>
      </div>
      {{ review.replyMessage }}
    </div>
    <div
      v-else
      class="review-actions"
    >
      <ds-button
        size="sm"
        @click="$emit('reply', review)"
      >
        Reply to user
      </ds-button>
    </div>
  </div>
</template>

<script>
import { DsBadge, DsButton } from '@/design-system/components'
import { formatDate } from '@/_helpers/util'

export default {
  name: 'Review',
  components: {
    DsBadge,
    DsButton,
  },
  props: {
    review: {
      type: Object,
      default: () => {
      },
    },
  },
  computed: {
    // Either list may be absent on a review.
    hasTags() {
      return (this.review.negativeTags || []).length > 0 || (this.review.positiveTags || []).length > 0
    },
    // Computed rather than a key assigned onto the `review` prop: that was both a child
    // mutating its prop and, with no Vue.set, a non-reactive key. It only looked correct
    // because created() ran before the first render and the deep watcher happened to flush
    // ahead of the render watcher. Now that it gates `disabled`, whether a control is
    // operable can't rest on watcher ordering.
    exceptionDisabled() {
      const monthAgo = new Date()
      monthAgo.setMonth(monthAgo.getMonth() - 1)
      return this.review.replyMessage != null || new Date(this.review.createdAt) < monthAgo
    },
  },
  methods: {
    formatDate(format, date) {
      return formatDate(format, date)
    },
  },
}
</script>

<style scoped lang="scss">
@import '~@/design-system/mixins';

.review {
  @include ds-inset-row;

  padding: 16px 18px;
}

.review-head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  padding-bottom: 12px;
  border-bottom: 1px solid $c-border-light;
}

.review-head-left {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.review-rating {
  display: inline-flex;
  gap: 1px;
  flex-shrink: 0;
}

.star {
  color: $c-border;

  &.filled {
    color: $c-orange;
    fill: $c-orange;
  }
}

.review-date {
  font-size: 12.5px;
  font-weight: 600;
  color: $c-text-sec;
  letter-spacing: -0.01em;
}

.review-head-right {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

// Sits with the disabled button, so the closed window doesn't read as a dead control.
.review-spent-reason {
  font-size: 11.5px;
  font-style: italic;
  color: $c-text-ter;
  text-align: right;
}

.review-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 12px;
}

.review-explanation {
  margin: 12px 0 0;
  font-size: 13.5px;
  line-height: 1.6;
  color: $c-text;
  white-space: pre-line;

  &.empty {
    font-style: italic;
    color: $c-text-ter;
  }
}

.review-reply {
  margin-top: 14px;
  padding: 12px 15px;
  background: $c-white;
  border: 1px solid $c-border;
  border-left: 3px solid $c-lavender;
  border-radius: 10px;
  font-size: 13px;
  line-height: 1.6;
  color: $c-text;
  white-space: pre-line;
}

.review-reply-head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
  font-weight: 700;
  font-size: 12.5px;
}

.review-reply-date {
  font-weight: 500;
  color: $c-text-sec;
}

.review-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 14px;
}
</style>
